import { useContext, useEffect, useRef, type ChangeEvent } from 'react';
import type { ApiError, ApiV2ErrorShape } from '~/api.ts';
import FileUploader, { type FileUploaderProps } from '~/components/FileUploader';
import FormLayout from '~/components/layout/templates/FormLayout';
import { useInfoConfirm } from '~/components/ui/Confirm';
import { formContext, useForm, type Form as FormType } from '~/components/ui/Form';
import type { ChoiceProps } from '~/components/ui/forms/Choice';
import {
    ChoiceField,
    FieldError,
    InputField,
    type BaseFieldProps,
    type InputFieldProps,
} from '~/components/ui/forms/Fields';
import { getFileNameFromUrl, isPlatformFile } from '~/utils/files';
import type { DataTestAttributes } from '~/utils/qa';
import { isValidUrl } from '~/utils/validate';

type FieldType = 'file' | 'link';

export type LinkOrFileFieldProps = BaseFieldProps &
    Omit<ChoiceProps, 'options'> & {
        defaultOption?: FieldType;
        inputFieldProps?: Omit<InputFieldProps, 'name' | 'label' | 'form'>;
        fileUploaderFieldProps?: Omit<FileUploaderProps, 'onUpload' | 'disabled'> & {
            defaultFileName?: string;
        };
        /** When provided, uploads immediately and syncs a URL. When omitted, keeps a File for submit-time upload. */
        onUpload?: (file: File) => Promise<string>;
    } & DataTestAttributes;

type LocalFormType = {
    [key: `fieldType_${string}`]: FieldType | undefined;
    link_url?: string;
    file_url?: string;
    file?: File;
};

function useEffectiveForm(propForm?: FormType<any, any>) {
    const contextForm = useContext(formContext);
    const form = propForm || contextForm;
    if (!form) {
        throw new Error(
            'No form instance found. Please provide a form via props or wrap the field in a <Form> component.',
        );
    }
    return form;
}

/* Reads a field message from `setError([...])` without relying on `getError`, I don't want to introduce more changes to useForm. */
function getSetErrorMessage(
    error: ApiError | null,
    ...fields: Array<'link_url' | 'file_url'>
): string | undefined {
    if (!Array.isArray(error) || error.length === 0) return undefined;
    if (typeof error[0] !== 'object' || error[0] === null || !('location' in error[0])) {
        return undefined;
    }
    const locations = new Set(fields.map((field) => `body.${field}`));
    return (error as ApiV2ErrorShape).find((entry) => locations.has(entry.location))?.message;
}

/**
 * Resolves the value synced to the parent form:
 * - `undefined` when no mode is selected
 * - `''` when a mode is selected but incomplete / invalid
 * - a URL string when valid
 * - a `File` when onUpload is omitted (deferred upload, e.g. apply flow)
 */
function resolveLinkOrFileValue(
    fieldType: FieldType | undefined,
    linkUrl: string | undefined,
    fileUrl: string | undefined,
    file: File | undefined,
): string | File | undefined {
    if (!fieldType) return undefined;

    if (fieldType === 'link') {
        const trimmed = linkUrl?.trim() ?? '';
        if (!trimmed || !isValidUrl(trimmed)) return '';
        return trimmed;
    }

    const uploaded = fileUrl?.trim() ?? '';
    if (uploaded) return uploaded;
    if (file) return file;
    return '';
}

export default function LinkOrFileField({
    form: propForm,
    // choice field props
    name,
    label,
    disabled = false,
    optional = false,
    inlineGap = 'small',
    // other props
    inputFieldProps,
    fileUploaderFieldProps,
    onUpload: uploadHandler,
    'data-test-component': testComponent = 'ui/forms/LinkOrFileField',
    'data-test-name': testName,
}: LinkOrFileFieldProps) {
    const form = useEffectiveForm(propForm);
    const fieldTypeName = `fieldType_${name}` as const;

    const localForm = useForm<LocalFormType>({});

    const fieldType = localForm.getField(fieldTypeName);
    const customErrorMessage = getSetErrorMessage(localForm.error, 'link_url', 'file_url');

    const hasValidActiveValue = Boolean(
        resolveLinkOrFileValue(
            fieldType,
            localForm.getField('link_url'),
            localForm.getField('file_url'),
            localForm.getField('file'),
        ),
    );

    const switchToLinkConfirm = useInfoConfirm(
        (ok) => {
            if (ok) localForm.handleChange(fieldTypeName, 'link');
        },
        { acceptLabel: 'Switch to link', cancelLabel: 'Cancel' },
    );

    const switchToFileConfirm = useInfoConfirm(
        (ok) => {
            if (ok) localForm.handleChange(fieldTypeName, 'file');
        },
        { acceptLabel: 'Switch to file', cancelLabel: 'Cancel' },
    );

    const handleFieldTypeChange = (next: FieldType) => {
        if (next === fieldType) return;
        if (!hasValidActiveValue) {
            localForm.handleChange(fieldTypeName, next);
            return;
        }
        if (next === 'link') {
            switchToLinkConfirm.show();
        } else {
            switchToFileConfirm.show();
        }
    };

    const setLinkInvalidURLError = (value: string) => {
        const link = value.trim();
        if (link && !isValidUrl(link)) {
            localForm.setError([
                {
                    message: 'Invalid URL',
                    location: 'body.link_url',
                    value,
                    code: 'INVALID_URL',
                },
            ]);
        } else {
            localForm.setError(null);
        }
    };

    const setUploadError = () => {
        localForm.setFields({ file_url: undefined, file: undefined }, true);
        localForm.setError([
            {
                message: 'Error uploading your file, please try again',
                location: 'body.file_url',
                value: null,
                code: 'FILE_UPLOAD_ERROR',
            },
        ]);
    };

    const uploadAndSetFileUrl = (upload: Promise<unknown>, file: File) =>
        upload
            .then((uploadedFile) => {
                if (typeof uploadedFile === 'string' && uploadedFile) {
                    localForm.setFields({ file_url: uploadedFile, file: undefined });
                    return { id: 1, name: file.name, url: uploadedFile };
                }
                setUploadError();
                return { id: 1, name: file.name };
            })
            .catch((err) => {
                setUploadError();
                throw err;
            });

    // Clear the inactive mode and file errors when switching.
    useEffect(() => {
        localForm.setError(null);
        if (fieldType === 'link') {
            localForm.setFields({ file_url: undefined, file: undefined }, true);
        } else if (fieldType === 'file') {
            localForm.setFields({ link_url: undefined }, true);
        }
    }, [fieldType]);

    // Sync resolved value to the parent form. While the link text is a non-empty, invalid
    // URL, skip the sync entirely instead of collapsing it to '' — otherwise a still-typing,
    // currently-invalid edit would silently overwrite (and on submit, wipe) a previously
    // saved value even though the inline "Invalid URL" error is showing.
    useEffect(() => {
        const linkValue = localForm.getField('link_url');
        const trimmedLink = linkValue?.trim() ?? '';
        const linkPendingFix =
            fieldType === 'link' && Boolean(trimmedLink) && !isValidUrl(trimmedLink);
        if (linkPendingFix) return;

        const next = resolveLinkOrFileValue(
            fieldType,
            linkValue,
            localForm.getField('file_url'),
            localForm.getField('file'),
        );
        if (form.getField(name) !== next) {
            form.handleChange(name, next);
        }
    }, [fieldType, localForm.data.link_url, localForm.data.file_url, localForm.data.file]);

    const parentValue = form.getField(name);
    const initializedFromParent = useRef(false);
    useEffect(() => {
        if (initializedFromParent.current) return;
        if (typeof parentValue !== 'string' || !parentValue) return;

        initializedFromParent.current = true;
        const isLink = !isPlatformFile(parentValue);

        localForm.setFields({
            [fieldTypeName]: isLink ? 'link' : 'file',
            link_url: isLink ? parentValue : undefined,
            file_url: isLink ? undefined : parentValue,
        } as LocalFormType);
        if (isLink) {
            setLinkInvalidURLError(parentValue);
        }
    }, [parentValue]);

    return (
        <FormLayout.Group
            gap='xsmall'
            data-test-component={testComponent}
            data-test-name={testName}>
            <ChoiceField
                key={name}
                label={label}
                optional={optional}
                form={localForm}
                name={fieldTypeName}
                disabled={disabled}
                inlineGap={inlineGap}
                onChange={(v) => handleFieldTypeChange(v as FieldType)}
                options={[
                    { value: 'link', label: 'Add a link', 'data-test-name': 'addLink' },
                    { value: 'file', label: 'Upload a PDF', 'data-test-name': 'uploadFile' },
                ]}
            />
            {fieldType === 'link' && (
                <InputField
                    form={localForm}
                    name='link_url'
                    data-test-name='linkUrl'
                    {...inputFieldProps}
                    disabled={disabled}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        localForm.handleChange('link_url', e.target.value);
                        setLinkInvalidURLError(e.target.value);
                    }}
                />
            )}

            {fieldType === 'file' && (
                <FileUploader
                    data-test-name='fileUploader'
                    key={String(
                        localForm.getField('file_url') ?? localForm.getField('file')?.name ?? '',
                    )}
                    file={
                        localForm.getField('file') || localForm.getField('file_url')
                            ? {
                                  id: 1,
                                  name:
                                      fileUploaderFieldProps?.defaultFileName ||
                                      localForm.getField('file')?.name ||
                                      getFileNameFromUrl(localForm.getField('file_url') as string),
                                  url: localForm.getField('file_url') as string,
                              }
                            : undefined
                    }
                    disabled={disabled || form.submitting}
                    onRemove={() => {
                        localForm.setError(null);
                        localForm.setFields({ file_url: undefined, file: undefined });
                    }}
                    errorText='Error uploading your file, please try again'
                    canView={localForm.getField('file') === undefined}
                    {...fileUploaderFieldProps}
                    onWrongFileType={() => {
                        localForm.setFields({ file_url: undefined, file: undefined }, true);
                        localForm.setError([
                            {
                                message: 'The type of the file you uploaded is not valid',
                                location: 'body.file_url',
                                value: null,
                                code: 'FILE_WRONG_TYPE',
                            },
                        ]);
                        fileUploaderFieldProps?.onWrongFileType?.();
                    }}
                    onTooBig={() => {
                        localForm.setFields({ file_url: undefined, file: undefined }, true);
                        localForm.setError([
                            {
                                message: 'File is too big',
                                location: 'body.file_url',
                                value: null,
                                code: 'FILE_TOO_BIG',
                            },
                        ]);
                        fileUploaderFieldProps?.onTooBig?.();
                    }}
                    onUpload={(file: File) => {
                        localForm.setError(null);
                        if (uploadHandler) {
                            return uploadAndSetFileUrl(uploadHandler(file), file);
                        }
                        localForm.setFields({ file, file_url: undefined });
                        return Promise.resolve({ id: 1, name: file.name });
                    }}
                />
            )}
            {customErrorMessage && <FieldError>{customErrorMessage}</FieldError>}
            {switchToLinkConfirm.render('Switch to a link?', 'Your uploaded file will be removed.')}
            {switchToFileConfirm.render('Switch to a file?', 'Your link will be removed.')}
        </FormLayout.Group>
    );
}
